import {createLocalEntity} from '../../../firebase/firestore/chartLocalEntities';

/** Map NER/chart shorthand to {@code EntityType} enum names expected by the API. */
const ENTITY_TYPE_ALIASES = {
    PER: 'PERSON',
    PERSON: 'PERSON',
    ORG: 'ORG',
    GPE: 'GPE',
    LOC: 'LOC',
    EVENT: 'EVENT',
    PRODUCT: 'PRODUCT',
    NORP: 'NORP',
};

export function normalizeEntityTypeForApi(raw, fallback = 'ORG') {
    if (raw == null || String(raw).trim() === '') {
        return fallback;
    }
    const upper = String(raw).trim().toUpperCase();
    return ENTITY_TYPE_ALIASES[upper] || upper;
}

function pendingLabel(ref) {
    if (ref == null) return null;
    const trimmed = String(ref).trim();
    if (!trimmed.toLowerCase().startsWith('pending:')) return null;
    return trimmed.slice('pending:'.length);
}

function resolvePendingRef(ref, pendingToRef) {
    if (ref == null) return ref;
    const trimmed = String(ref).trim();
    const lower = trimmed.toLowerCase();
    if (pendingToRef.has(trimmed)) return pendingToRef.get(trimmed);
    if (pendingToRef.has(lower)) return pendingToRef.get(lower);
    const label = pendingLabel(trimmed);
    if (label != null) {
        if (pendingToRef.has(label)) return pendingToRef.get(label);
        if (pendingToRef.has(label.toLowerCase())) return pendingToRef.get(label.toLowerCase());
    }
    return trimmed;
}

/**
 * Mint D-id entities for create_local_entity ops and rewrite pending:* arrow endpoints.
 * Local creates run in parallel — they are independent Firestore writes.
 *
 * @param {Array<object>} mutations
 * @returns {Promise<Array<object>>}
 */
export async function materializeChartMutations(mutations) {
    const list = Array.isArray(mutations) ? mutations : [];
    const pendingToRef = new Map();

    const createJobs = list
        .map((mutation, index) => ({mutation, index}))
        .filter(({mutation}) => mutation?.op === 'create_local_entity');

    const createdByIndex = new Map();
    await Promise.all(createJobs.map(async ({mutation, index}) => {
        const label = mutation.label;
        if (!label) {
            throw new Error('create_local_entity requires label');
        }
        const response = await createLocalEntity({
            canonicalName: label,
            entityType: normalizeEntityTypeForApi(mutation.entityType),
        });
        const entityRef = response?.entityRef;
        if (!entityRef) {
            throw new Error(`Could not create entity "${label}"`);
        }
        const pendingKey = `pending:${label}`;
        pendingToRef.set(pendingKey, entityRef);
        pendingToRef.set(pendingKey.toLowerCase(), entityRef);
        pendingToRef.set(label, entityRef);
        pendingToRef.set(label.toLowerCase(), entityRef);
        createdByIndex.set(index, {
            op: 'add_entity_card',
            entityRef,
            label,
            suggestedAnchor: mutation.suggestedAnchor,
        });
    }));

    const materialized = list.map((mutation, index) => {
        if (mutation?.op === 'create_local_entity') {
            return createdByIndex.get(index);
        }
        return mutation;
    });

    return materialized.map((mutation) => {
        if (mutation?.op !== 'add_arrow') return mutation;
        return {
            ...mutation,
            fromEntityRef: resolvePendingRef(mutation.fromEntityRef, pendingToRef),
            toEntityRef: resolvePendingRef(mutation.toEntityRef, pendingToRef),
        };
    });
}

/**
 * Collect add_arrow mutations that reference a pending create label.
 *
 * @param {Array<object>} allMutations
 * @param {object} createMutation
 * @returns {Array<object>}
 */
export function relatedPendingArrowMutations(allMutations, pendingMutation) {
    const label = pendingMutation?.label;
    if (!label) return [];
    const pendingRef = `pending:${label}`;
    const pendingLower = pendingRef.toLowerCase();
    return (allMutations || []).filter((mutation) => {
        if (mutation?.op !== 'add_arrow') return false;
        const from = String(mutation.fromEntityRef || '').toLowerCase();
        const to = String(mutation.toEntityRef || '').toLowerCase();
        return from === pendingLower || to === pendingLower;
    });
}

/**
 * Arrows in the plan that connect to a concrete add_entity_card ref (or its pending label).
 *
 * @param {Array<object>} allMutations
 * @param {object} entityMutation add_entity_card or create_local_entity row
 * @returns {Array<object>}
 */
export function relatedArrowMutations(allMutations, entityMutation) {
    if (!entityMutation) return [];
    if (entityMutation.op === 'create_local_entity') {
        return relatedPendingArrowMutations(allMutations, entityMutation);
    }
    const entityRef = entityMutation.entityRef != null
        ? String(entityMutation.entityRef).trim()
        : '';
    const label = entityMutation.label != null ? String(entityMutation.label).trim() : '';
    const pendingRef = label ? `pending:${label}` : '';
    const pendingLower = pendingRef.toLowerCase();
    const entityLower = entityRef.toLowerCase();
    return (allMutations || []).filter((mutation) => {
        if (mutation?.op !== 'add_arrow') return false;
        const from = String(mutation.fromEntityRef || '').trim();
        const to = String(mutation.toEntityRef || '').trim();
        if (entityRef && (from === entityRef || to === entityRef)) return true;
        if (entityLower && (from.toLowerCase() === entityLower || to.toLowerCase() === entityLower)) {
            return true;
        }
        if (pendingRef && (from === pendingRef || to === pendingRef
            || from.toLowerCase() === pendingLower || to.toLowerCase() === pendingLower)) {
            return true;
        }
        return false;
    });
}

/**
 * Build add_entity_card + related arrows after the analyst picks a KB candidate.
 *
 * @param {object} pendingMutation pick_entity or create_local_entity row
 * @param {string} entityRef resolved KB / D-id ref
 * @param {Array<object>} allMutations full plan mutations
 * @returns {Array<object>}
 */
export function buildResolvedAddBatch(pendingMutation, entityRef, allMutations) {
    const label = pendingMutation?.label;
    if (!entityRef || !label) {
        throw new Error('Resolved entity requires label and entityRef');
    }
    const pendingToRef = new Map();
    const pendingKey = `pending:${label}`;
    pendingToRef.set(pendingKey, entityRef);
    pendingToRef.set(pendingKey.toLowerCase(), entityRef);
    pendingToRef.set(label, entityRef);
    pendingToRef.set(label.toLowerCase(), entityRef);

    const addCard = {
        op: 'add_entity_card',
        entityRef,
        label,
        suggestedAnchor: pendingMutation.suggestedAnchor,
    };
    const arrows = relatedPendingArrowMutations(allMutations, pendingMutation).map((mutation) => ({
        ...mutation,
        fromEntityRef: resolvePendingRef(mutation.fromEntityRef, pendingToRef),
        toEntityRef: resolvePendingRef(mutation.toEntityRef, pendingToRef),
    }));
    return [addCard, ...arrows];
}
